/* eslint-disable @typescript-eslint/no-explicit-any */
import Styles from "./WebHeader.module.scss";
import Logo from "../../../../assets/images/icons/mainlogo.svg";
import LogoMobile from "../../../../assets/images/icons/mainlogo-mobile.png";
import DarkThemeLogo from "../../../../assets/images/icons/mainlogoDark.svg";
import DarkThemeLogoMobile from "../../../../assets/images/icons/mainlogoDark-mobile.png";
import {
  AppBar,
  Box,
  Container,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "../../../../shared/button/Button";
import ThemeSwitch from "../../../../shared/themeswitch/ThemeSwitch";
import { useNavigate } from "react-router-dom";
import { useGlobalSettings } from "../../../../context/GlobalSettingsContext";
import { useEffect } from "react";
import { useThemeContext } from "../../../../context/ThemeContext";
import {
  getRoleData,
  getToken,
} from "../../../../shared/functions/commonfunction";
import SHVRLogo from "../../../../assets/images/icons/shvr-logo.svg";
import SHVRLogoMobile from "../../../../assets/images/icons/shvr-logo-mobile.png";
import SHVRLogoDark from "../../../../assets/images/icons/shvr-logo-dark.svg";
import SHVRLogoDarkMobile from "../../../../assets/images/icons/shvr-logo-dark-mobile.png";

interface WebHeaderProps {
  mobileIconOnly?: boolean;
}

const WebHeader = ({ mobileIconOnly = true }: WebHeaderProps) => {
  const { t } = useTranslation("home");
  const navigate = useNavigate();
  const theme = useTheme();
  const { toggleTheme } = useThemeContext();
  const { language, setLanguage, fontSize, setFontSize, resetFontSize } =
    useGlobalSettings();

  const scrollToBenefits = () => {
    const benefitsSection = document.getElementById("benefits-section");
    if (benefitsSection) {
      const scrollPosition = benefitsSection.offsetTop - 150; // Reduced offset to 150px
      window.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--dynamic-font-size",
      fontSize.toString()
    );
  }, [fontSize]);

  const handleLanguageChange = (event: SelectChangeEvent) => {
    const newLanguage = event.target.value as string;
    setLanguage(newLanguage);

    // Dispatch custom event to trigger page reload
    const languageChangeEvent = new CustomEvent("languageChange", {
      detail: { language: newLanguage },
    });
    window.dispatchEvent(languageChangeEvent);
  };

  const handleTextSizeChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    switch (value) {
      case "a-":
        setFontSize(0.9);
        break;
      case "a+":
        setFontSize(1.1);
        break;
      default:
        resetFontSize();
        break;
    }
  };

  const SelectComponent = ({
    value,
    onChange,
    options = [],
    label,
    controlWidth = 76,
  }: any) => {
    return (
      <FormControl
        size="small"
        sx={{ mr: 1, flex: `0 0 ${controlWidth}px`, width: controlWidth }}
      >
        <Select
          inputProps={{ "aria-label": label }}
          MenuProps={{ disableScrollLock: true }}
          value={value}
          onChange={onChange}
          variant="outlined"
          sx={{
            height: 30,
            width: `${controlWidth}px`,
            minWidth: `${controlWidth}px`,
            maxWidth: `${controlWidth}px`,
            borderRadius: "20px !important",
            padding: "0 12px",
            fontFamily: "Poppins, sans-serif",
            fontSize: "14px",
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            flexShrink: 0, // Prevent shrinking
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 !important",
              paddingRight: "22px !important",
              paddingLeft: "8px !important",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            "& svg": {
              right: 8,
              position: "absolute",
              color: theme.palette.text.primary,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor:
                theme.palette.mode === "light" ? "#1C1E21" : "#ffffff",
              borderWidth: "0.5px !important",
              borderRadius: "20px !important",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor:
                theme.palette.mode === "light" ? "#1C1E21" : "#ffffff",
              borderWidth: "0.5px !important",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor:
                theme.palette.mode === "light" ? "#1C1E21" : "#ffffff",
              borderWidth: "0.5px !important",
            },
          }}
          IconComponent={(props) => (
            <svg
              {...props}
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
              style={{ marginRight: 4 }}
            >
              <path
                d="M1.5 5.5l6 6 6-6"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
              />
            </svg>
          )}
        >
          {options.map((opt: any) => (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.background.paper,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "light" ? "#f5f5f5" : "#2c2c2c",
                },
              }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const languageOptions = [
    { value: "en", label: "Eng" },
    { value: "hi", label: "हिंदी" },
  ];

  const fontOptions = [
    { value: "a-", label: "A-" },
    { value: "a", label: "A" },
    { value: "a+", label: "A+" },
  ];

  const getCurrentFontSize = () => {
    if (fontSize < 1) return "a-";
    if (fontSize > 1) return "a+";
    return "a";
  };

  const roleData = getRoleData();
  const isSchoolUser = roleData?.role_code == "SCHOOL_USER";
  const isOtherUser =
    roleData?.role_code == "NATIONAL_NODAL" ||
    roleData?.role_code == "STATE_NODAL" ||
    roleData?.role_code == "DISTRICT_NODAL";

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor:
          theme.palette.mode === "dark"
            ? "#1A1D21"
            : theme.palette.background.paper,
        boxShadow: "none",
        borderBottom: "none",
      }}
    >
      <Container maxWidth={false} disableGutters>
        <AppBar
          position="sticky"
          sx={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: "none",
            zIndex: 1100,
            top: 0,
            justifyContent: "center",
            alignItems: "center",
          }}
          className={`${Styles.header} ${
            mobileIconOnly ? Styles.mobile_icon_only : ""
          }`}
        >
          <Toolbar
            className={Styles.toolbar}
          >
            <div
              className={Styles.logo_container}
              onClick={() => navigate("/")}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  navigate("/");
                }
              }}
              role="link"
              tabIndex={0}
              aria-label="Go to homepage"
            >
              <img
                src={theme.palette.mode === "dark" ? DarkThemeLogo : Logo}
                srcSet={
                  theme.palette.mode === "dark"
                    ? `${DarkThemeLogoMobile} 767w, ${DarkThemeLogo} 1200w`
                    : `${LogoMobile} 767w, ${Logo} 1200w`
                }
                sizes="(max-width: 767px) 40px, 50px"
                alt="SHVR logo"
                className={Styles.logo_image}
              />
              <Typography
                className={Styles.logo_text}
                sx={{
                  whiteSpace: "nowrap",
                  color: theme.palette.text.primary,
                  fontSize: {xs: "12px",sm: "12px", md: "12px", lg: "14px", xl: "16px"}
                }}
              >
                {t("header.mainlogotext")}
                <br />
                <Typography
                  component="span"
                  sx={{ fontWeight: "400", fontSize: {xs: "12px", sm: "12px", md: "12px", lg: "14px", xl: "16px"}}}
                  
                >
                  {t("header.sublogotext")}
                </Typography>
              </Typography>
              {mobileIconOnly && (
                <img
                  src={theme.palette.mode === "dark" ? SHVRLogoDark : SHVRLogo}
                  srcSet={
                    theme.palette.mode === "dark"
                      ? `${SHVRLogoDarkMobile} 767w, ${SHVRLogoDark} 1200w`
                      : `${SHVRLogoMobile} 767w, ${SHVRLogo} 1200w`
                  }
                  sizes="(max-width: 767px) 46px, 97px"
                  alt="Swachh Evam Harit Vidyalaya Rating"
                  className={Styles.mobile_shvr_icon}
                />
              )}
            </div>
            <div className={Styles.header_action}>
              {/* <div
                className={Styles.contentButton}
                style={{
                  fontSize: `calc(0.875rem * var(--dynamic-font-size, 1))`,
                  color: theme.palette.text.primary,
                }}
              >
                {t("header.lastUpdated")}
              </div>
              <div
                className={Styles.contentButton}
                style={{
                  fontSize: `calc(0.875rem * var(--dynamic-font-size, 1))`,
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                }}
                onClick={scrollToBenefits}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    scrollToBenefits();
                  }
                }}
              >
                {t("header.screenReaderAccess")}|{t("header.skipToMain")}
              </div> */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0, // Prevent the entire controls box from shrinking
                  flexWrap: "nowrap", // Prevent wrapping to next line
                  gap: 0.5, // Small gap between controls
                }}
              >
                <SelectComponent
                  value={language}
                  onChange={handleLanguageChange}
                  options={languageOptions}
                  label="Select language"
                  controlWidth={82}
                />

                <SelectComponent
                  value={getCurrentFontSize()}
                  onChange={handleTextSizeChange}
                  options={fontOptions}
                  label="Select text size"
                  controlWidth={70}
                />
                <ThemeSwitch
                  checked={theme.palette.mode === "dark"}
                  onChange={() => {
                    const newMode =
                      theme.palette.mode === "dark" ? "light" : "dark";
                    sessionStorage.setItem("theme", newMode);
                    toggleTheme();
                  }}
                />
              </Box>
              <Box
                className={Styles.shvr_logo_container}
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src={theme.palette.mode === "dark" ? SHVRLogoDark : SHVRLogo}
                  srcSet={
                    theme.palette.mode === "dark"
                      ? `${SHVRLogoDarkMobile} 767w, ${SHVRLogoDark} 1200w`
                      : `${SHVRLogoMobile} 767w, ${SHVRLogo} 1200w`
                  }
                  sizes="(max-width: 767px) 46px, 97px"
                  alt="Swachh Evam Harit Vidyalaya Rating logo"
                  style={{ margin: "0px 0px 0px 20px" }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "start",
                    alignItems: "start",
                  }}
                >
                  <Typography
                    className={Styles.header_text}
                    sx={{
                      whiteSpace: "nowrap",
                      color: theme.palette.text.primary,
                      textAlign: "start",
                      marginLeft: "10px !important",
                      fontSize: {sm: "12px", md: "12px", lg: "14px", xl: "16px"}
                    }}
                  >
                    {t("header.scheme_first_name")}{" "}
                    {t("header.scheme_last_name")}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: "400",
                      marginLeft: "10px !important",
                      fontSize: {sm: "12px", lg: "14px", xl: "16px"}
                    }}
                  >
                    {t("header.scheme_sub_heading")}
                  </Typography>
                </Box>
              </Box>

              {/* {!getToken() && (
                <Box sx={{ width: "170px" }}>
                  <Button
                    text={t("header.loginSignup")}
                    onClick={() => {
                      navigate("account/login");
                    }}
                    sx={{ whiteSpace: "nowrap" }}
                  />
                </Box>
              )} */}
            </div>
          </Toolbar>
        </AppBar>
      </Container>
    </Box>
  );
};

export default WebHeader;
